import { useContext, useState, useEffect, useCallback } from 'react'
import { DashboardContext } from '@/context/DashboardContext'
import { getNextResetUTC, getTimeRemaining, type TimeRemaining } from '@/utils/timer'
import { useGiveUpWindow } from './useGiveUpWindow'
import { useOfferReset } from './useOfferReset'

export interface OverflowTimerState {
  isActive: boolean
  countdown: TimeRemaining
}

/**
 * Client fallback for the moments before the server's window lands. Give-up is open the
 * whole offer day now (2026-08-31 — cap-api's `giveUpOpenHoursBeforeReset()` retired the
 * old partial-day restriction), so the fallback just counts down to the reset, matching
 * what the server reports once its response lands.
 */
function giveUpFallback(nextReset: string | null): OverflowTimerState {
  const resetsAt = getNextResetUTC(nextReset)
  return { isActive: true, countdown: getTimeRemaining(resetsAt) }
}

/**
 * Give-up window state + countdown. Server-authoritative (Feature 1d): uses the
 * configurable window from cap-api when available, falling back to the client-side
 * computation if the fetch hasn't landed / failed. When the window is OPEN we count
 * down to the offer's own reset; when CLOSED, to the next open time.
 */
export function useOverflowTimer(): OverflowTimerState {
  const ctx = useContext(DashboardContext)
  const { window } = useGiveUpWindow(60000, ctx?.selectedOfferId ?? null)
  const { nextReset } = useOfferReset()

  const compute = useCallback((): OverflowTimerState => {
    if (window) {
      const target = window.isOpen ? new Date(window.resetsAtUTC) : new Date(window.opensAtUTC)
      return { isActive: window.isOpen, countdown: getTimeRemaining(target) }
    }
    return giveUpFallback(nextReset)
  }, [window, nextReset])

  const [state, setState] = useState<OverflowTimerState>(compute)

  useEffect(() => {
    setState(compute())
    const interval = setInterval(() => setState(compute()), 1000)
    return () => clearInterval(interval)
  }, [compute])

  return state
}
